// The content stream interpreter (ISO 32000-1 §8–§9): paths, clipping, colour, text, images, forms, shadings and patterns drawn onto a 2-D canvas.

import { DEVICE_CMYK, DEVICE_GRAY, DEVICE_RGB, makeColorSpace, makeFunction, type ColorSpace } from "./color";
import { type PdfDocument, type PdfPage } from "./document";
import { loadFont, type GlyphRec, type PdfFont } from "./font";
import { decodeImage, type PdfImage } from "./image";
import { Dict, Lexer, PCmd, PName, PRef, Stream, type PObj, type XRef } from "./parse";

export type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
export type Matrix = [number, number, number, number, number, number];

export interface TextRun {
    /** The text of the show operation, character by character (what a viewer selects). */
    chars: string[];
    /** The start of each glyph along the baseline, in px of a 1:1 view (and one final end). */
    offs: number[];
    /** The baseline start in the 1:1 view. */
    x: number;
    y: number;
    /** The baseline's direction in the view (unit vector). */
    dx: number;
    dy: number;
    /** Font size in 1:1 view px, and the font's ascent / descent as fractions of it. */
    size: number;
    ascent: number;
    descent: number;
    font: string;
    /** The fill colour the text was shown in (CSS), when it is a plain colour. */
    color?: string;
    /** Offset in the page content just after the operator that made it (for the page's own content only; a form's runs carry the offset of its `Do`). */
    at?: number;
}

/** One vector shape painted on the page: its bounding box in px of a 1:1 view (the same space as TextRun). */
export interface ShapeRun {
    x: number;
    y: number;
    w: number;
    h: number;
    stroked: boolean;
    filled: boolean;
    /** Line width in the same px. */
    lineWidth: number;
    /** Fill colour as CSS when it is a plain colour. */
    fill?: string;
    /** See TextRun.at. */
    at?: number;
}

/** One picture painted on the page: its bounding box in px of a 1:1 view (the same space as TextRun). */
export interface ImageRun {
    x: number;
    y: number;
    w: number;
    h: number;
    /** A stencil mask (a one-colour shape), not a photograph. */
    stencil: boolean;
    /** See TextRun.at. */
    at?: number;
}

export interface RenderOptions {
    /** Pixels per point; the canvas is `size * scale` pixels. */
    scale: number;
    /** Extra rotation in degrees (multiple of 90) on top of the page's own. */
    rotation?: number;
    /** Draw the page's annotations (their appearance streams). */
    annotations?: boolean;
    /** Collect the text runs of the page here. */
    text?: TextRun[];
    /** Collects the lines, boxes and other vector shapes painted, in px of a 1:1 view, so an editor can pick them. */
    shapes?: ShapeRun[];
    /** Collects where each picture was painted (box in px of a 1:1 view), so an editor can offer to move or remove it. */
    images?: ImageRun[];
    /** Called between operations; return true to stop. */
    shouldStop?: () => boolean;
    /** Paper colour (default white); null leaves the canvas transparent. */
    background?: string | null;
    /** Only collect text: nothing is painted for glyphs and images. */
    textOnly?: boolean;
    /** Called when an operator fails (the page goes on). */
    onError?: (op: string, e: unknown) => void;
    /** Operators executed between yields to the event loop (0 = never yield). */
    yieldEvery?: number;
}

const identity = (): Matrix => [1, 0, 0, 1, 0, 0];
const mul = (a: Matrix, b: Matrix): Matrix => [a[0] * b[0] + a[1] * b[2], a[0] * b[1] + a[1] * b[3], a[2] * b[0] + a[3] * b[2], a[2] * b[1] + a[3] * b[3], a[4] * b[0] + a[5] * b[2] + b[4], a[4] * b[1] + a[5] * b[3] + b[5]];
const applyM = (m: Matrix, x: number, y: number): [number, number] => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
const invert = (m: Matrix): Matrix | null => { const d = m[0] * m[3] - m[1] * m[2]; if (!d) return null; return [m[3] / d, -m[1] / d, -m[2] / d, m[0] / d, (m[2] * m[5] - m[3] * m[4]) / d, (m[1] * m[4] - m[0] * m[5]) / d]; };
const toMatrix = (o: PObj, xref: XRef): Matrix | null => { if (!Array.isArray(o) || o.length < 6) return null; const v = o.slice(0, 6).map((x) => xref.resolve(x)); return v.every((x) => typeof x === "number") ? (v as Matrix) : null; };
const rgbStr = (c: [number, number, number]) => `rgb(${c[0]},${c[1]},${c[2]})`;

interface GState {
    ctm: Matrix;
    fillCS: ColorSpace;
    strokeCS: ColorSpace;
    fill: number[];
    stroke: number[];
    fillPattern: Dict | Stream | null;
    strokePattern: Dict | Stream | null;
    lineWidth: number;
    lineCap: CanvasLineCap;
    lineJoin: CanvasLineJoin;
    miter: number;
    dash: number[];
    dashPhase: number;
    fillAlpha: number;
    strokeAlpha: number;
    blend: string;
    // text state
    charSp: number;
    wordSp: number;
    hScale: number;
    leading: number;
    font: PdfFont | null;
    fontSize: number;
    renderMode: number;
    rise: number;
}

const newState = (ctm: Matrix): GState => ({ ctm, fillCS: DEVICE_GRAY, strokeCS: DEVICE_GRAY, fill: [0], stroke: [0], fillPattern: null, strokePattern: null, lineWidth: 1, lineCap: "butt", lineJoin: "miter", miter: 10, dash: [], dashPhase: 0, fillAlpha: 1, strokeAlpha: 1, blend: "source-over", charSp: 0, wordSp: 0, hScale: 1, leading: 0, font: null, fontSize: 0, renderMode: 0, rise: 0 });
const cloneState = (g: GState): GState => ({ ...g, ctm: [...g.ctm] as Matrix, fill: [...g.fill], stroke: [...g.stroke], dash: [...g.dash] });

const BLEND: Record<string, GlobalCompositeOperation> = { Normal: "source-over", Compatible: "source-over", Multiply: "multiply", Screen: "screen", Overlay: "overlay", Darken: "darken", Lighten: "lighten", ColorDodge: "color-dodge", ColorBurn: "color-burn", HardLight: "hard-light", SoftLight: "soft-light", Difference: "difference", Exclusion: "exclusion", Hue: "hue", Saturation: "saturation", Color: "color", Luminosity: "luminosity" };

const fontCache = new WeakMap<Dict, PdfFont>();
const imageCache = new WeakMap<Stream, Promise<PdfImage | null>>();

/** Optional content: the groups switched off in the default configuration. */
function hiddenGroups(doc: PdfDocument): Set<number> {
    const hidden = new Set<number>();
    const ocp = doc.catalog.dict("OCProperties");
    const d = ocp?.dict("D");
    if (!ocp || !d) return hidden;
    const base = d.name("BaseState");
    if (base === "OFF") { for (const g of ocp.arr("OCGs") ?? []) if (g instanceof PRef) hidden.add(g.num); for (const g of d.arr("ON") ?? []) if (g instanceof PRef) hidden.delete(g.num); }
    for (const g of d.arr("OFF") ?? []) if (g instanceof PRef) hidden.add(g.num);
    return hidden;
}

class Interp {
    private stack: GState[] = [];
    gs: GState;
    private path = new Path2D();
    /** Bounding box of the path being built, in user space: minX, minY, maxX, maxY. */
    private pb: [number, number, number, number] | null = null;
    private grow(...xy: number[]) { for (let i = 0; i < xy.length; i += 2) { const x = xy[i]!, y = xy[i + 1]!; const b = this.pb; if (!b) this.pb = [x, y, x, y]; else { if (x < b[0]) b[0] = x; if (y < b[1]) b[1] = y; if (x > b[2]) b[2] = x; if (y > b[3]) b[3] = y; } } }
    private hasPath = false;
    private clipRule: CanvasFillRule | null = null;
    private tm: Matrix = identity();
    private tlm: Matrix = identity();
    private textClip: Path2D | null = null;
    private depth = 0;
    private ops = 0;
    stopped = false;
    private marked: boolean[] = [];
    private hiddenDepth = 0;
    private uncolored = false;
    /** Page-level interpreter only: remember where in the page content each run came from. */
    trackAt = false;
    private curAt: number | undefined = undefined;

    constructor(private readonly doc: PdfDocument, private readonly xref: XRef, private readonly ctx: Ctx, private readonly base: Matrix, private readonly view1: Matrix, private readonly opts: RenderOptions, private readonly hidden: Set<number>) {
        this.gs = newState(identity());
    }

    private setT(m: Matrix) { const t = mul(m, this.base); this.ctx.setTransform(t[0], t[1], t[2], t[3], t[4], t[5]); }

    async run(data: Uint8Array, res: Dict | null) {
        if (this.depth > 12) return;
        this.depth++;
        const lx = new Lexer(data);
        let args: PObj[] = [];
        for (;;) {
            if (this.stopped) break;
            const t = lx.next();
            if (t === undefined) break;
            if (!(t instanceof PCmd)) { args.push(t); if (args.length > 64) args.shift(); continue; }
            const op = t.cmd;
            if (this.trackAt && this.depth === 1) this.curAt = lx.pos;
            if (op === "[" || op === "<<") { args.push(readCompound(lx, op, this.xref)); if (args.length > 64) args.shift(); continue; }
            if (op === "true") { args.push(true); continue; }
            if (op === "false") { args.push(false); continue; }
            if (op === "null") { args.push(null); continue; }
            if (op === "BI") { await this.inlineImage(lx, res); args = []; continue; }
            try { await this.exec(op, args, res); } catch (e) { if ((e as Error)?.message === "stop") break; this.opts.onError?.(op, e); }
            args = [];
            if (this.opts.yieldEvery && ++this.ops % this.opts.yieldEvery === 0) {
                await new Promise<void>((r) => setTimeout(r, 0));
                if (this.opts.shouldStop?.()) { this.stopped = true; break; }
            }
        }
        this.depth--;
    }

    private num(a: PObj[], i: number): number { const v = a[i]; return typeof v === "number" ? v : 0; }

    private visible(): boolean { return this.hiddenDepth === 0; }

    private async exec(op: string, a: PObj[], res: Dict | null) {
        const ctx = this.ctx, gs = this.gs;
        const n = (i: number) => this.num(a, i);
        switch (op) {
            case "q": this.stack.push(cloneState(gs)); ctx.save(); break;
            case "Q": if (this.stack.length) { this.gs = this.stack.pop()!; ctx.restore(); } break;
            case "cm": gs.ctm = mul([n(0), n(1), n(2), n(3), n(4), n(5)], gs.ctm); break;
            case "w": gs.lineWidth = n(0); break;
            case "J": gs.lineCap = (["butt", "round", "square"] as const)[n(0)] ?? "butt"; break;
            case "j": gs.lineJoin = (["miter", "round", "bevel"] as const)[n(0)] ?? "miter"; break;
            case "M": gs.miter = n(0); break;
            case "d": { const arr = Array.isArray(a[0]) ? (a[0] as PObj[]).filter((x) => typeof x === "number") as number[] : []; gs.dash = arr.some((x) => x > 0) ? arr : []; gs.dashPhase = n(1); break; }
            case "ri": case "i": break;
            case "gs": this.extGState(a[0], res); break;
            // paths
            case "m": this.grow(n(0), n(1)); this.path.moveTo(n(0), n(1)); this.hasPath = true; this.cx = n(0); this.cy = n(1); break;
            case "l": this.grow(n(0), n(1)); this.path.lineTo(n(0), n(1)); this.cx = n(0); this.cy = n(1); break;
            case "c": this.grow(n(0), n(1), n(2), n(3), n(4), n(5)); this.path.bezierCurveTo(n(0), n(1), n(2), n(3), n(4), n(5)); this.cx = n(4); this.cy = n(5); break;
            case "v": this.grow(n(0), n(1), n(2), n(3)); this.path.bezierCurveTo(this.cx, this.cy, n(0), n(1), n(2), n(3)); this.cx = n(2); this.cy = n(3); break;
            case "y": this.grow(n(0), n(1), n(2), n(3)); this.path.bezierCurveTo(n(0), n(1), n(2), n(3), n(2), n(3)); this.cx = n(2); this.cy = n(3); break;
            case "h": this.path.closePath(); break;
            case "re": { const x = n(0), y = n(1), w = n(2), h = n(3); this.grow(x, y, x + w, y + h); this.path.moveTo(x, y); this.path.lineTo(x + w, y); this.path.lineTo(x + w, y + h); this.path.lineTo(x, y + h); this.path.closePath(); this.hasPath = true; this.cx = x; this.cy = y; break; }
            case "S": await this.paint(false, true, "nonzero"); break;
            case "s": this.path.closePath(); await this.paint(false, true, "nonzero"); break;
            case "f": case "F": await this.paint(true, false, "nonzero"); break;
            case "f*": await this.paint(true, false, "evenodd"); break;
            case "B": await this.paint(true, true, "nonzero"); break;
            case "B*": await this.paint(true, true, "evenodd"); break;
            case "b": this.path.closePath(); await this.paint(true, true, "nonzero"); break;
            case "b*": this.path.closePath(); await this.paint(true, true, "evenodd"); break;
            case "n": await this.paint(false, false, "nonzero"); break;
            case "W": this.clipRule = "nonzero"; break;
            case "W*": this.clipRule = "evenodd"; break;
            // colour
            case "CS": gs.strokeCS = makeColorSpace(a[0] ?? null, res, this.xref); gs.stroke = gs.strokeCS.initial(); gs.strokePattern = null; break;
            case "cs": gs.fillCS = makeColorSpace(a[0] ?? null, res, this.xref); gs.fill = gs.fillCS.initial(); gs.fillPattern = null; break;
            case "SC": case "SCN": this.setColor(a, true, res); break;
            case "sc": case "scn": this.setColor(a, false, res); break;
            case "G": if (!this.uncolored) { gs.strokeCS = DEVICE_GRAY; gs.stroke = [n(0)]; gs.strokePattern = null; } break;
            case "g": if (!this.uncolored) { gs.fillCS = DEVICE_GRAY; gs.fill = [n(0)]; gs.fillPattern = null; } break;
            case "RG": if (!this.uncolored) { gs.strokeCS = DEVICE_RGB; gs.stroke = [n(0), n(1), n(2)]; gs.strokePattern = null; } break;
            case "rg": if (!this.uncolored) { gs.fillCS = DEVICE_RGB; gs.fill = [n(0), n(1), n(2)]; gs.fillPattern = null; } break;
            case "K": if (!this.uncolored) { gs.strokeCS = DEVICE_CMYK; gs.stroke = [n(0), n(1), n(2), n(3)]; gs.strokePattern = null; } break;
            case "k": if (!this.uncolored) { gs.fillCS = DEVICE_CMYK; gs.fill = [n(0), n(1), n(2), n(3)]; gs.fillPattern = null; } break;
            case "sh": await this.shading(a[0], res); break;
            // text
            case "BT": this.tm = identity(); this.tlm = identity(); this.textClip = null; break;
            case "ET": if (this.textClip) { this.setT(gs.ctm); ctx.clip(this.textClip); this.textClip = null; } break;
            case "Tc": gs.charSp = n(0); break;
            case "Tw": gs.wordSp = n(0); break;
            case "Tz": gs.hScale = n(0) / 100; break;
            case "TL": gs.leading = n(0); break;
            case "Tf": { const nm = a[0] instanceof PName ? a[0].name : ""; gs.font = this.getFont(nm, res); gs.fontSize = n(1); break; }
            case "Tr": gs.renderMode = n(0); break;
            case "Ts": gs.rise = n(0); break;
            case "Td": this.tlm = mul([1, 0, 0, 1, n(0), n(1)], this.tlm); this.tm = [...this.tlm] as Matrix; break;
            case "TD": gs.leading = -n(1); this.tlm = mul([1, 0, 0, 1, n(0), n(1)], this.tlm); this.tm = [...this.tlm] as Matrix; break;
            case "Tm": this.tm = [n(0), n(1), n(2), n(3), n(4), n(5)]; this.tlm = [...this.tm] as Matrix; break;
            case "T*": this.nextLine(); break;
            case "Tj": await this.showText([typeof a[0] === "string" ? a[0] : ""], res); break;
            case "TJ": await this.showText(Array.isArray(a[0]) ? (a[0] as PObj[]).filter((x) => typeof x === "string" || typeof x === "number") as Array<string | number> : [], res); break;
            case "'": this.nextLine(); await this.showText([typeof a[0] === "string" ? a[0] : ""], res); break;
            case '"': gs.wordSp = n(0); gs.charSp = n(1); this.nextLine(); await this.showText([typeof a[2] === "string" ? a[2] : ""], res); break;
            case "d0": break;
            case "d1": this.uncolored = true; break;
            // xobjects
            case "Do": await this.doXObject(a[0] instanceof PName ? a[0].name : "", res); break;
            // marked content
            case "BMC": this.marked.push(false); break;
            case "BDC": {
                let hide = false;
                if (a[0] instanceof PName && a[0].name === "OC") {
                    const prop = a[1];
                    let ref: PObj | undefined = prop;
                    if (prop instanceof PName) ref = res?.dict("Properties")?.raw(prop.name);
                    if (ref instanceof PRef && this.hidden.has(ref.num)) hide = true;
                }
                this.marked.push(hide);
                if (hide) this.hiddenDepth++;
                break;
            }
            case "EMC": if (this.marked.pop()) this.hiddenDepth--; break;
            default: break;
        }
    }

    private cx = 0;
    private cy = 0;

    private setColor(a: PObj[], stroke: boolean, res: Dict | null) {
        if (this.uncolored) return;
        const gs = this.gs;
        const cs = stroke ? gs.strokeCS : gs.fillCS;
        if (cs.name === "Pattern") {
            const last = a[a.length - 1];
            if (last instanceof PName) {
                const p = res?.dict("Pattern")?.get(last.name) ?? null;
                const pat = p instanceof Stream ? p : p instanceof Dict ? p : null;
                const comps = a.slice(0, -1).filter((x) => typeof x === "number") as number[];
                if (stroke) { gs.strokePattern = pat; gs.stroke = comps; } else { gs.fillPattern = pat; gs.fill = comps; }
            }
            return;
        }
        const comps = a.filter((x) => typeof x === "number") as number[];
        if (stroke) { gs.stroke = comps; gs.strokePattern = null; } else { gs.fill = comps; gs.fillPattern = null; }
    }

    private extGState(nameObj: PObj | undefined, res: Dict | null) {
        if (!(nameObj instanceof PName)) return;
        const d = res?.dict("ExtGState")?.dict(nameObj.name);
        if (!d) return;
        const gs = this.gs;
        for (const k of d.keys()) {
            const v = d.get(k);
            switch (k) {
                case "LW": if (typeof v === "number") gs.lineWidth = v; break;
                case "LC": if (typeof v === "number") gs.lineCap = (["butt", "round", "square"] as const)[v] ?? "butt"; break;
                case "LJ": if (typeof v === "number") gs.lineJoin = (["miter", "round", "bevel"] as const)[v] ?? "miter"; break;
                case "ML": if (typeof v === "number") gs.miter = v; break;
                case "D": if (Array.isArray(v) && Array.isArray(this.xref.resolve(v[0]))) { const arr = (this.xref.resolve(v[0]) as PObj[]).filter((x) => typeof x === "number") as number[]; gs.dash = arr.some((x) => x > 0) ? arr : []; gs.dashPhase = typeof v[1] === "number" ? v[1] : 0; } break;
                case "CA": if (typeof v === "number") gs.strokeAlpha = v; break;
                case "ca": if (typeof v === "number") gs.fillAlpha = v; break;
                case "BM": { const nm = v instanceof PName ? v.name : Array.isArray(v) && v[0] instanceof PName ? v[0].name : "Normal"; gs.blend = BLEND[nm] ?? "source-over"; break; }
                case "Font": if (Array.isArray(v)) { const fd = this.xref.resolve(v[0]); if (fd instanceof Dict) { gs.font = this.fontOf(fd); gs.fontSize = typeof v[1] === "number" ? v[1] : gs.fontSize; } } break;
                default: break;
            }
        }
    }

    private fontOf(d: Dict): PdfFont {
        let f = fontCache.get(d);
        if (!f) { try { f = loadFont(d, this.xref); } catch { f = null as unknown as PdfFont; } if (f) fontCache.set(d, f); }
        return f as PdfFont;
    }

    private getFont(name: string, res: Dict | null): PdfFont | null {
        const d = res?.dict("Font")?.dict(name);
        return d ? this.fontOf(d) : null;
    }

    private fillStyle(stroke: boolean): string | CanvasPattern | CanvasGradient | null {
        const gs = this.gs;
        const pat = stroke ? gs.strokePattern : gs.fillPattern;
        const cs = stroke ? gs.strokeCS : gs.fillCS;
        if (cs.name === "Pattern") return pat ? this.patternStyle(pat, stroke) : null;
        return rgbStr(cs.rgb(stroke ? gs.stroke : gs.fill));
    }

    private applyAlpha(stroke: boolean) { this.ctx.globalAlpha = stroke ? this.gs.strokeAlpha : this.gs.fillAlpha; this.ctx.globalCompositeOperation = this.gs.blend as GlobalCompositeOperation; }

    private async paint(fill: boolean, stroke: boolean, rule: CanvasFillRule) {
        const ctx = this.ctx, gs = this.gs;
        const path = this.path;
        if (this.hasPath && this.visible()) {
            this.setT(gs.ctm);
            if (fill) {
                const st = this.fillStyle(false);
                if (st) {
                    if (gs.fillCS.name === "Pattern" && gs.fillPattern && !(typeof st === "string")) { this.applyAlpha(false); ctx.fillStyle = st; ctx.fill(path, rule); }
                    else if (typeof st === "string") { this.applyAlpha(false); ctx.fillStyle = st; ctx.fill(path, rule); }
                }
            }
            if (stroke) {
                const st = this.fillStyle(true);
                if (st) {
                    this.applyAlpha(true);
                    ctx.strokeStyle = st;
                    this.applyStroke();
                    ctx.stroke(path);
                }
            }
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = "source-over";
        }
        if (this.opts.shapes && this.pb && (fill || stroke) && this.hasPath && this.visible()) {
            const d = mul(gs.ctm, this.view1);
            const b = this.pb;
            const pts = [applyM(d, b[0], b[1]), applyM(d, b[2], b[1]), applyM(d, b[0], b[3]), applyM(d, b[2], b[3])];
            const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
            const lw = stroke ? Math.max(0.5, gs.lineWidth * Math.hypot(d[0], d[1])) : 0;
            const st = fill ? this.fillStyle(false) : null;
            this.opts.shapes.push({ x: Math.min(...xs) - lw / 2, y: Math.min(...ys) - lw / 2, w: Math.max(...xs) - Math.min(...xs) + lw, h: Math.max(...ys) - Math.min(...ys) + lw, stroked: stroke, filled: fill, lineWidth: lw, ...(typeof st === "string" ? { fill: st } : {}), ...(this.curAt !== undefined ? { at: this.curAt } : {}) });
        }
        if (this.clipRule) { this.setT(gs.ctm); ctx.clip(path, this.clipRule); this.clipRule = null; }
        this.path = new Path2D();
        this.pb = null;
        this.hasPath = false;
    }

    private applyStroke() {
        const ctx = this.ctx, gs = this.gs;
        const sc = Math.hypot(gs.ctm[0], gs.ctm[1]) * Math.hypot(this.base[0], this.base[1]) || 1;
        const sc2 = Math.sqrt(Math.abs(gs.ctm[0] * gs.ctm[3] - gs.ctm[1] * gs.ctm[2])) * Math.hypot(this.base[0], this.base[1]) || sc;
        // hairlines are one device pixel wide
        ctx.lineWidth = Math.max(gs.lineWidth, 1 / (sc2 || 1));
        ctx.lineCap = gs.lineCap;
        ctx.lineJoin = gs.lineJoin;
        ctx.miterLimit = Math.max(1, gs.miter);
        ctx.setLineDash(gs.dash.length ? (gs.dash.length % 2 ? [...gs.dash, ...gs.dash] : gs.dash) : []);
        ctx.lineDashOffset = gs.dashPhase;
    }

    // ---- text -----------------------------------------------------------------------------------------------------------------------

    private nextLine() { this.tlm = mul([1, 0, 0, 1, 0, -this.gs.leading], this.tlm); this.tm = [...this.tlm] as Matrix; }

    private async showText(parts: Array<string | number>, res: Dict | null) {
        const gs = this.gs;
        const font = gs.font;
        if (!font) return;
        const ctx = this.ctx;
        const fs = gs.fontSize, th = gs.hScale;
        const runChars: string[] = [], offs: number[] = [];
        let startX = 0, startY = 0, dirX = 1, dirY = 0, first = true;
        const size1 = Math.hypot(...applyDir(mul(this.tm, mul(gs.ctm, this.view1)), 0, fs));
        const vis = this.visible();
        let advanceAlong = 0;
        for (const part of parts) {
            if (typeof part === "number") {
                const adj = (-part / 1000) * fs;
                if (font.vertical) this.tm = mul([1, 0, 0, 1, 0, adj], this.tm); else { this.tm = mul([1, 0, 0, 1, adj * th, 0], this.tm); }
                advanceAlong += adj * th;
                continue;
            }
            for (const g of font.decode(part)) {
                const trm = mul([fs * th, 0, 0, fs, 0, gs.rise], mul(this.tm, gs.ctm));
                if (this.opts.text && !font.type3) {
                    const dev = mul(trm, this.view1);
                    const o = applyM(dev, 0, 0);
                    if (first) { startX = o[0]; startY = o[1]; const e = applyM(dev, 1, 0); const len = Math.hypot(e[0] - o[0], e[1] - o[1]) || 1; dirX = (e[0] - o[0]) / len; dirY = (e[1] - o[1]) / len; first = false; }
                    offs.push((o[0] - startX) * dirX + (o[1] - startY) * dirY);
                    runChars.push(g.unicode || " ");
                }
                if (vis && gs.renderMode !== 3 && !this.opts.textOnly) await this.drawGlyph(font, g, trm, res);
                else if (vis && gs.renderMode === 3 && false) void 0;
                let tx = 0, ty = 0;
                if (font.vertical) ty = (g.adv ?? -1000) / 1000 * fs + gs.charSp + (g.space ? gs.wordSp : 0);
                else tx = ((g.width / 1000) * fs + gs.charSp + (g.space ? gs.wordSp : 0)) * th;
                this.tm = mul([1, 0, 0, 1, tx, ty], this.tm);
                advanceAlong += tx;
            }
        }
        void ctx;
        if (this.opts.text && runChars.length) {
            const dev = mul([fs * th, 0, 0, fs, 0, gs.rise], mul(this.tm, mul(gs.ctm, this.view1)));
            const e = applyM(dev, 0, 0);
            offs.push((e[0] - startX) * dirX + (e[1] - startY) * dirY);
            const fb = font.fontBox;
            const fillNow = this.fillStyle(false);
            this.opts.text.push({ chars: runChars, offs, x: startX, y: startY, dx: dirX, dy: dirY, size: size1, ascent: fb ? Math.max(0.7, fb[3]! / 1000) : 0.8, descent: fb ? Math.min(0.3, -fb[1]! / 1000) : 0.2, font: font.base, ...(typeof fillNow === "string" ? { color: fillNow } : {}), ...(this.curAt !== undefined ? { at: this.curAt } : {}) });
        }
        void advanceAlong;
    }

    private async drawGlyph(font: PdfFont, g: GlyphRec, trm: Matrix, res: Dict | null) {
        const ctx = this.ctx, gs = this.gs;
        const mode = gs.renderMode;
        let t = trm;
        if (font.vertical && g.vx !== undefined && g.vy !== undefined) t = mul([1, 0, 0, 1, -g.vx / 1000, -g.vy / 1000], trm);
        if (font.type3) { await this.type3Glyph(font, g, t, res); return; }
        const path = font.path(g);
        if (path) {
            this.setT(t);
            const fillIt = mode === 0 || mode === 2 || mode === 4 || mode === 6;
            const strokeIt = mode === 1 || mode === 2 || mode === 5 || mode === 6;
            if (fillIt) { const st = this.fillStyle(false); if (typeof st === "string" || st) { this.applyAlpha(false); ctx.fillStyle = st!; ctx.fill(path); } }
            if (strokeIt) {
                const st = this.fillStyle(true);
                if (st) {
                    this.applyAlpha(true);
                    ctx.strokeStyle = st;
                    // the stroke width is in user space; the transform here is the glyph's em space
                    const k = Math.hypot(t[0], t[1]) / Math.max(1e-6, Math.hypot(gs.ctm[0], gs.ctm[1]));
                    const sc = Math.hypot(this.base[0], this.base[1]);
                    ctx.lineWidth = Math.max(gs.lineWidth, 1 / sc) / (k || 1);
                    ctx.lineJoin = gs.lineJoin;
                    ctx.stroke(path);
                }
            }
            if (mode >= 4) { if (!this.textClip) this.textClip = new Path2D(); const m = new DOMMatrix([t[0], t[1], t[2], t[3], t[4], t[5]]); const g2 = new DOMMatrix([gs.ctm[0], gs.ctm[1], gs.ctm[2], gs.ctm[3], gs.ctm[4], gs.ctm[5]]).inverse(); this.textClip.addPath(path, g2.multiply(m)); }
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = "source-over";
            return;
        }
        // no outline: the browser's font
        const text = font.fallbackText(g);
        if (!text || text === " " || mode === 1 && false) return;
        const st = this.fillStyle(false);
        if (!st) return;
        this.setT(t);
        ctx.save();
        ctx.scale(0.01, -0.01);
        ctx.font = font.cssFont();
        ctx.textBaseline = "alphabetic";
        this.applyAlpha(false);
        ctx.fillStyle = st;
        const w = (ctx as CanvasRenderingContext2D).measureText(text).width / 100;
        const want = g.width / 1000;
        if (w > 0.01 && want > 0.01) { const sx = Math.min(1.6, Math.max(0.55, want / w)); ctx.scale(sx, 1); }
        ctx.fillText(text, 0, 0);
        ctx.restore();
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
    }

    private async type3Glyph(font: PdfFont, g: GlyphRec, trm: Matrix, _res: Dict | null) {
        const t3 = font.type3!;
        const proc = font.type3Proc(g.code);
        if (!proc) return;
        const saveGs = this.gs;
        const savePath = this.path, saveHas = this.hasPath, saveTm = this.tm, saveTlm = this.tlm, saveUnc = this.uncolored;
        const saveStack = this.stack;
        this.stack = [];
        this.ctx.save();
        this.gs = cloneState(saveGs);
        // glyph space → text space → user space: the font matrix, then the text rendering matrix (which has the font size folded in)
        this.gs.ctm = mul(t3.matrix as Matrix, mul([1 / 1, 0, 0, 1, 0, 0], trm));
        this.uncolored = false;
        this.path = new Path2D();
        this.hasPath = false;
        const r = t3.resources ?? _res;
        // d1 glyphs take the current fill colour for everything
        const data = proc.bytes();
        const isD1 = /\sd1\s*(\n|\r|$)|d1\b/.test(new TextDecoder("latin1").decode(data.subarray(0, 80)));
        if (isD1) { this.gs.stroke = [...this.gs.fill]; this.gs.strokeCS = this.gs.fillCS; }
        await this.run(data, r);
        this.ctx.restore();
        this.gs = saveGs;
        this.path = savePath; this.hasPath = saveHas; this.tm = saveTm; this.tlm = saveTlm; this.uncolored = saveUnc;
        this.stack = saveStack;
    }

    // ---- xobjects, images, shadings -----------------------------------------------------------------------------------------------------

    private async doXObject(name: string, res: Dict | null) {
        const xo = res?.dict("XObject")?.get(name);
        if (!(xo instanceof Stream)) return;
        const oc = xo.dict.raw("OC");
        if (oc instanceof PRef && this.hidden.has(oc.num)) return;
        if (!this.visible()) return;
        const sub = xo.dict.name("Subtype");
        if (sub === "Image") { if (!this.opts.textOnly) await this.drawImage(xo, res); return; }
        if (sub === "Form") await this.form(xo, res);
    }

    private async form(s: Stream, res: Dict | null) {
        const d = s.dict;
        const m = toMatrix(d.get("Matrix"), this.xref) ?? identity();
        const bb = d.arr("BBox");
        const ctx = this.ctx;
        this.stack.push(cloneState(this.gs));
        ctx.save();
        const saveStack = this.stack;
        this.stack = [];
        const savePath = this.path, saveHas = this.hasPath;
        this.path = new Path2D(); this.hasPath = false;
        this.gs.ctm = mul(m, this.gs.ctm);
        if (bb && bb.length >= 4) {
            const [x0, y0, x1, y1] = bb.slice(0, 4).map((v) => (typeof v === "number" ? v : 0)) as [number, number, number, number];
            this.setT(this.gs.ctm);
            const p = new Path2D();
            p.rect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
            ctx.clip(p);
        }
        const fres = d.dict("Resources") ?? res;
        await this.run(s.bytes(), fres);
        this.stack = saveStack;
        this.path = savePath; this.hasPath = saveHas;
        ctx.restore();
        this.gs = this.stack.pop()!;
    }

    private async drawImage(s: Stream, res: Dict | null) {
        let p = imageCache.get(s);
        if (!p) { p = decodeImage(s, res, this.xref).catch(() => null); imageCache.set(s, p); }
        const img = await p;
        if (img) this.paintImage(img);
    }

    private tintCache = new Map<string, HTMLCanvasElement | OffscreenCanvas>();
    private paintImage(img: PdfImage) {
        const ctx = this.ctx, gs = this.gs;
        if (this.opts.images) {
            const d = mul(gs.ctm, this.view1);
            const pts = [applyM(d, 0, 0), applyM(d, 1, 0), applyM(d, 0, 1), applyM(d, 1, 1)];
            const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
            this.opts.images.push({ x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys), stencil: Boolean(img.stencil), ...(this.curAt !== undefined ? { at: this.curAt } : {}) });
        }
        const m = mul([1 / img.width, 0, 0, -1 / img.height, 0, 1], gs.ctm);
        this.setT(m);
        this.applyAlpha(false);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        let src = img.canvas as CanvasImageSource;
        if (img.stencil) {
            const st = this.fillStyle(false);
            if (typeof st !== "string") { ctx.globalAlpha = 1; return; }
            const key = `${st}|${img.width}x${img.height}`;
            let tc = this.tintCache.get(key);
            if (!tc) {
                tc = typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(img.width, img.height) : (() => { const c = document.createElement("canvas"); c.width = img.width; c.height = img.height; return c; })();
                const c2 = tc.getContext("2d") as CanvasRenderingContext2D;
                c2.drawImage(img.canvas as CanvasImageSource, 0, 0);
                c2.globalCompositeOperation = "source-in";
                c2.fillStyle = st;
                c2.fillRect(0, 0, img.width, img.height);
                if (this.tintCache.size > 16) this.tintCache.clear();
                this.tintCache.set(key, tc);
            }
            src = tc as CanvasImageSource;
        }
        ctx.drawImage(src, 0, 0);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
    }

    private async inlineImage(lx: Lexer, res: Dict | null) {
        const d = new Dict(this.xref);
        for (;;) {
            const k = lx.next();
            if (k === undefined) return;
            if (k instanceof PCmd && k.cmd === "ID") break;
            if (!(k instanceof PName)) continue;
            let v = lx.next();
            if (v === undefined) return;
            if (v instanceof PCmd && v.cmd === "ID") break;
            if (v instanceof PCmd && (v.cmd === "[" || v.cmd === "<<")) v = readCompound(lx, v.cmd, this.xref);
            else if (v instanceof PCmd && v.cmd === "true") v = true;
            else if (v instanceof PCmd && v.cmd === "false") v = false;
            d.set(expandAbbrev(k.name), v);
        }
        const b = lx.buf;
        let start = lx.pos;
        if (b[start] === 13 && b[start + 1] === 10) start += 2; else if (b[start] === 13 || b[start] === 10 || b[start] === 32 || b[start] === 9) start += 1;
        const w = d.num("Width"), h = d.num("Height");
        const f = d.get("Filter");
        let end = -1;
        if (f === null && w > 0 && h > 0) {
            const cs = makeColorSpace(abbrevCS(d.get("ColorSpace")) ?? new PName("DeviceGray"), res, this.xref);
            const im = d.get("ImageMask") === true;
            const bpc = im ? 1 : d.num("BitsPerComponent", 8);
            const n = im ? 1 : cs.n || 1;
            end = start + h * ((w * n * bpc + 7) >> 3);
        }
        const ws = (c: number | undefined) => c === undefined || c === 32 || c === 10 || c === 13 || c === 9 || c === 0;
        const isEI = (i: number) => b[i] === 0x45 && b[i + 1] === 0x49 && ws(b[i - 1]) && ws(b[i + 2]);
        if (end < 0 || end > b.length) { end = b.length; for (let i = start; i + 1 < b.length; i++) if (isEI(i)) { end = i; break; } }
        const raw = b.subarray(start, Math.min(end, b.length));
        let q = Math.min(end, b.length);
        while (q + 1 < b.length && !(b[q] === 0x45 && b[q + 1] === 0x49)) q++;
        lx.pos = Math.min(b.length, q + 2);
        const cs = d.get("ColorSpace");
        if (cs !== null) d.set("ColorSpace", abbrevCS(cs) ?? cs);
        const s = new Stream(d, raw);
        const img = this.opts.textOnly ? null : await decodeImage(s, res, this.xref, true).catch(() => null);
        if (img && this.visible()) this.paintImage(img);
    }

    private async shading(nameObj: PObj | undefined, res: Dict | null) {
        if (!(nameObj instanceof PName) || !this.visible()) return;
        const sh = res?.dict("Shading")?.get(nameObj.name);
        const d = sh instanceof Stream ? sh.dict : sh instanceof Dict ? sh : null;
        if (!d) return;
        const ctx = this.ctx;
        this.setT(this.gs.ctm);
        this.applyAlpha(false);
        const style = this.shadeStyle(sh as Dict | Stream, this.gs.ctm);
        if (style) {
            ctx.fillStyle = style;
            // paint the clip region: a huge rectangle in user space
            const inv = invert(this.gs.ctm);
            void inv;
            ctx.fillRect(-1e5, -1e5, 2e5, 2e5);
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
    }

    private shadeStyle(sh: Dict | Stream, _ctm: Matrix): CanvasGradient | null {
        const d = sh instanceof Stream ? sh.dict : sh;
        const type = d.num("ShadingType");
        const cs = makeColorSpace(d.get("ColorSpace"), null, this.xref);
        const fnObj = d.get("Function");
        const fn = fnObj ? makeFunction(fnObj, this.xref) : null;
        const coords = (d.arr("Coords") ?? []).map((x) => { const v = this.xref.resolve(x); return typeof v === "number" ? v : 0; });
        const dom = (d.arr("Domain") ?? [0, 1]).map((x) => { const v = this.xref.resolve(x); return typeof v === "number" ? v : 0; });
        const ext = (d.arr("Extend") ?? [false, false]).map((x) => this.xref.resolve(x) === true);
        if (!fn || (type !== 2 && type !== 3)) return null;
        const ctx = this.ctx;
        let grad: CanvasGradient;
        if (type === 2) grad = ctx.createLinearGradient(coords[0]!, coords[1]!, coords[2]!, coords[3]!);
        else grad = ctx.createRadialGradient(coords[0]!, coords[1]!, coords[2]!, coords[3]!, coords[4]!, coords[5]!);
        const steps = 48;
        for (let i = 0; i <= steps; i++) {
            const t = dom[0]! + ((dom[1]! - dom[0]!) * i) / steps;
            const rgb = cs.rgb(fn([t]));
            grad.addColorStop(i / steps, rgbStr(rgb));
        }
        void ext;
        return grad;
    }

    private patternStyle(pat: Dict | Stream, stroke: boolean): CanvasPattern | CanvasGradient | string | null {
        void stroke;
        const d = pat instanceof Stream ? pat.dict : pat;
        const ptype = d.num("PatternType");
        const pm = toMatrix(d.get("Matrix"), this.xref) ?? identity();
        if (ptype === 2) {
            const sh = d.get("Shading");
            if (!(sh instanceof Dict || sh instanceof Stream)) return null;
            // the pattern space is the page's default space (not the CTM): gradient coordinates are in pattern space, so apply pm through the transform at fill time
            const g = this.shadeStyle(sh, pm);
            if (!g) return null;
            return g;
        }
        if (ptype === 1 && pat instanceof Stream) {
            const bb = (d.arr("BBox") ?? []).map((x) => { const v = this.xref.resolve(x); return typeof v === "number" ? v : 0; });
            const xs = d.num("XStep") || Math.abs((bb[2] ?? 1) - (bb[0] ?? 0)), ys = d.num("YStep") || Math.abs((bb[3] ?? 1) - (bb[1] ?? 0));
            if (!xs || !ys) return null;
            const paint = d.num("PaintType", 1);
            const scale = Math.hypot(this.base[0], this.base[1]) * Math.hypot(pm[0], pm[1]);
            const tw = Math.max(1, Math.min(2048, Math.ceil(Math.abs(xs) * scale))), th = Math.max(1, Math.min(2048, Math.ceil(Math.abs(ys) * scale)));
            const tile = typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(tw, th) : (() => { const c = document.createElement("canvas"); c.width = tw; c.height = th; return c; })();
            const tctx = tile.getContext("2d") as Ctx;
            const sub = new Interp(this.doc, this.xref, tctx, [tw / Math.abs(xs), 0, 0, -th / Math.abs(ys), -(bb[0] ?? 0) * (tw / Math.abs(xs)), th + (bb[1] ?? 0) * (th / Math.abs(ys))], this.view1, { ...this.opts, text: undefined }, this.hidden);
            if (paint === 2) { sub.gs.fill = [...this.gs.fill]; sub.gs.fillCS = this.gs.fillCS.base ?? DEVICE_RGB; sub.gs.stroke = sub.gs.fill; sub.gs.strokeCS = sub.gs.fillCS; sub.uncolored = true; }
            void sub.run(pat.bytes(), d.dict("Resources"));
            // run is async but the tile content paints synchronously unless it holds images; give it a tick via a sync best effort
            const cp = this.ctx.createPattern(tile as CanvasImageSource, "repeat");
            if (cp) {
                // pattern space → device: base × pm; the tile is tw × th device px spanning (xs, ys) pattern units
                const dm = mul([Math.abs(xs) / tw, 0, 0, -Math.abs(ys) / th, bb[0] ?? 0, (bb[1] ?? 0) + Math.abs(ys)], mul(pm, this.pageBase()));
                const inv = invert(this.base);
                const full = inv ? mul(dm, inv) : dm;
                cp.setTransform(new DOMMatrix([full[0], full[1], full[2], full[3], full[4], full[5]]));
                return cp;
            }
        }
        return null;
    }
    private pageBase(): Matrix { return this.base; }
}

function readCompound(lx: Lexer, open: string, xref: XRef): PObj {
    const read = (): PObj | undefined => {
        const t = lx.next();
        if (t === undefined) return undefined;
        if (t instanceof PCmd) {
            if (t.cmd === "[" || t.cmd === "<<") return readCompound(lx, t.cmd, xref);
            if (t.cmd === "true") return true;
            if (t.cmd === "false") return false;
            if (t.cmd === "null") return null;
        }
        return t;
    };
    if (open === "[") {
        const arr: PObj[] = [];
        for (;;) { const t = read(); if (t === undefined || (t instanceof PCmd && t.cmd === "]")) break; arr.push(t); }
        return arr;
    }
    const d = new Dict(xref);
    for (;;) {
        const k = read();
        if (k === undefined || (k instanceof PCmd && k.cmd === ">>")) break;
        if (!(k instanceof PName)) continue;
        const v = read();
        if (v === undefined) break;
        if (v instanceof PCmd && v.cmd === ">>") { d.set(k.name, null); break; }
        d.set(k.name, v);
    }
    return d;
}

function abbrevCS(o: PObj): PObj | null {
    if (o instanceof PName) return new PName(CSABB[o.name] ?? o.name);
    if (Array.isArray(o) && o[0] instanceof PName && o[0].name === "I") return ["Indexed" as unknown as PObj, ...o.slice(1)].map((x, i) => (i === 0 ? new PName("Indexed") : i === 1 ? abbrevCS(x) ?? x : x));
    return o;
}

function applyDir(m: Matrix, x: number, y: number): [number, number] { return [m[0] * x + m[2] * y, m[1] * x + m[3] * y]; }

const ABBREV: Record<string, string> = { BPC: "BitsPerComponent", CS: "ColorSpace", D: "Decode", DP: "DecodeParms", F: "Filter", H: "Height", IM: "ImageMask", I: "Interpolate", W: "Width", L: "Length" };
const CSABB: Record<string, string> = { G: "DeviceGray", RGB: "DeviceRGB", CMYK: "DeviceCMYK", I: "Indexed" };
function expandAbbrev(k: string): string { return ABBREV[k] ?? k; }
void CSABB;

/** Renders a page to a canvas context whose size is (page size × scale) pixels. */
export async function renderPage(doc: PdfDocument, page: PdfPage, ctx: Ctx, opts: RenderOptions): Promise<void> {
    const xref = doc.xref;
    const rotation = opts.rotation ?? 0;
    const base = page.viewMatrix(opts.scale, rotation);
    const view1 = page.viewMatrix(1, rotation);
    const [pw, ph] = (() => { const s = page.size; return (page.rotate + rotation) % 180 === 90 ? [s[1], s[0]] : s; })();
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (opts.background !== null) { ctx.fillStyle = opts.background ?? "#ffffff"; ctx.fillRect(0, 0, Math.ceil(pw * opts.scale), Math.ceil(ph * opts.scale)); }
    // clip to the page box
    const cb = page.cropBox;
    ctx.setTransform(base[0], base[1], base[2], base[3], base[4], base[5]);
    const clip = new Path2D();
    clip.rect(cb[0], cb[1], cb[2] - cb[0], cb[3] - cb[1]);
    ctx.clip(clip);
    const hidden = hiddenGroups(doc);
    const it = new Interp(doc, xref, ctx, base, view1, opts, hidden);
    it.trackAt = true;
    try {
        await it.run(page.content(), page.resources);
        if (opts.annotations !== false && !it.stopped) await drawAnnotations(doc, page, ctx, base, view1, opts, hidden);
    } finally {
        ctx.restore();
    }
}

/** Draws each annotation's normal appearance stream mapped onto its /Rect. */
async function drawAnnotations(doc: PdfDocument, page: PdfPage, ctx: Ctx, base: Matrix, view1: Matrix, opts: RenderOptions, hidden: Set<number>) {
    const xref = doc.xref;
    for (const a of page.annots()) {
        const flags = a.num("F");
        if (flags & 2 || flags & 32) continue; // hidden or NoView
        const sub = a.name("Subtype");
        if (sub === "Popup") continue;
        const oc = a.raw("OC");
        if (oc instanceof PRef && hidden.has(oc.num)) continue;
        const ap = a.dict("AP");
        let n: PObj = ap?.get("N") ?? null;
        if (n instanceof Dict && !(n instanceof Stream)) { const as = a.name("AS"); n = (as ? n.get(as) : null) ?? null; }
        if (!(n instanceof Stream)) continue;
        const rect = (a.arr("Rect") ?? []).map((x) => { const v = xref.resolve(x); return typeof v === "number" ? v : 0; });
        if (rect.length < 4) continue;
        const r = [Math.min(rect[0]!, rect[2]!), Math.min(rect[1]!, rect[3]!), Math.max(rect[0]!, rect[2]!), Math.max(rect[1]!, rect[3]!)];
        const bb = (n.dict.arr("BBox") ?? [0, 0, 1, 1]).map((x) => { const v = xref.resolve(x); return typeof v === "number" ? v : 0; });
        const m = toMatrix(n.dict.get("Matrix"), xref) ?? identity();
        // the transformed BBox's bounds
        const pts = [applyM(m, bb[0]!, bb[1]!), applyM(m, bb[2]!, bb[1]!), applyM(m, bb[2]!, bb[3]!), applyM(m, bb[0]!, bb[3]!)];
        const bx0 = Math.min(...pts.map((p) => p[0])), bx1 = Math.max(...pts.map((p) => p[0])), by0 = Math.min(...pts.map((p) => p[1])), by1 = Math.max(...pts.map((p) => p[1]));
        const sx = bx1 - bx0 ? (r[2]! - r[0]!) / (bx1 - bx0) : 1, sy = by1 - by0 ? (r[3]! - r[1]!) / (by1 - by0) : 1;
        const A: Matrix = [sx, 0, 0, sy, r[0]! - bx0 * sx, r[1]! - by0 * sy];
        const it = new Interp(doc, xref, ctx, base, view1, { ...opts, text: undefined }, hidden);
        it.gs.ctm = A;
        ctx.save();
        // an annotation is drawn as a form: the form's own matrix applies inside `form`
        await (it as unknown as { form(s: Stream, r: Dict | null): Promise<void> }).form(n, page.resources);
        ctx.restore();
    }
}

export { DEVICE_GRAY };
